/**
 * LiveChat integration.
 *
 * Its own bundle rather than part of app.js: the block used to sit at the tail
 * of the theme's long DOMContentLoaded handler, so an exception thrown anywhere
 * earlier in that handler took the chat down with it - silently, and with
 * nothing in the failure pointing at LiveChat.
 *
 * Settings come from Settings -> LiveChat via wp_localize_script; see
 * inc/helpers/class-livechat-helper.php. Plan and rationale for every decision
 * here: docs/fe-livechat-plan.md.
 *
 * Three things are load-bearing and easy to undo by accident:
 *
 *   1. The widget must load even when nothing is clicked. Until tracking.js
 *      runs, the visitor does not exist for LiveChat - no session, invisible in
 *      the agent's traffic list, unreachable by any campaign. The five
 *      interaction events cannot be relied on: a phone has no mousemove.
 *
 *   2. A widget reporting visibility 'hidden' is not on the page at all, so a
 *      greeting has nowhere to render. Leaving that state needs a minimize or
 *      maximize call.
 *
 *   3. Session variables have one owner, collectSessionVariables(). Sending a
 *      second set_session_variables from anywhere else silently wipes the first.
 *
 * Not yet here (S6-S8 of the plan): campaign flags with their expiry, player
 * identification, and the rollout kill switch.
 */
function initLiveChat() {
    // Top window only.
    //
    // The cashier loads the PSP in an iframe, and after the PSP's redirect that
    // iframe holds our own /transaction-result page - a normal WordPress page, so
    // everything the theme enqueues runs inside it as well. Without this guard
    // the widget boots in the payment window: a chat bubble in a narrow frame
    // over the result of a payment, and a campaign aimed at the result URL would
    // open a chat window inside it. The parent page is where the chat belongs,
    // and it is already running its own copy.
    //
    // Comparing the two references is safe across origins - reading a property
    // of window.top would not be. If even the comparison is refused, treat that
    // as being framed: a missing widget is a smaller problem than one in the
    // wrong place.
    try {
        if (window.top !== window.self) {
            return;
        }
    } catch (e) {
        return;
    }

    // The button is optional. It used to gate the whole integration with an
    // early return, so a page whose template does not render one had no widget
    // at all: no session, nobody visible in the agent's traffic list, and no
    // greeting able to reach the visitor. The query now decides one thing only -
    // whether there is anything to bind a click to. An empty NodeList iterates
    // zero times, so no guard is needed below.
    const liveChatButtons = document.querySelectorAll('.live-chat-btn');

    // Connection settings come from the LiveChat settings page, printed by
    // wp_localize_script. There is deliberately no hardcoded fallback: a
    // constant left here would quietly send sessions to the old account on any
    // page where the config failed to print, and a chat talking to the wrong
    // license is indistinguishable from one that works. Missing config means no
    // widget, which is at least visible.
    var config = window.livechatConfig || {};
    if (!config.license) {
        return;
    }

    window.__lc = window.__lc || {};
    window.__lc.group = Number(config.group) || 0;
    window.__lc.license = Number(config.license);
    window.__lc.integration_name = "manual_onboarding";

    // Support offer (window.bbSupport below): the reason a page asked for help, kept here so
    // the full set sent on ready carries it - set_session_variables replaces the whole set.
    let supportVars = {};
    let widgetReady = false;
    window.__lc.product_name = "livechat";
    // Opt out of LiveChat's auto-load; tracking.js is injected on first user
    // interaction instead (see deferred loader below).
    window.__lc.asyncInit = true;

    (function(n, t, c) {
        function i(n) {
            return e._h ? e._h.apply(null, n) : e._q.push(n)
        }
        var e = {
            _q: [],
            _h: null,
            _v: "2.0",
            on: function() {
                i(["on", c.call(arguments)])
            },
            once: function() {
                i(["once", c.call(arguments)])
            },
            off: function() {
                i(["off", c.call(arguments)])
            },
            get: function() {
                if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
                return i(["get", c.call(arguments)])
            },
            call: function() {
                i(["call", c.call(arguments)])
            },
            init: function() {
                var n = t.createElement("script");
                n.async = !0, n.type = "text/javascript", n.src = "https://cdn.livechatinc.com/tracking.js", t.head.appendChild(n);
            }
        };
        !n.__lc.asyncInit && e.init(), n.LiveChatWidget = n.LiveChatWidget || e
    }(window, document, [].slice));

    liveChatButtons.forEach(function(button) {
        button.addEventListener('click', function() {
            if (window.LiveChatWidget) {
                LiveChatWidget.call('maximize');
            }
        });
    });

    // The signed-in player, if this page carries them. `userobject` is localised
    // by the account-login block during its own render, so it exists only on
    // pages that render that block and only once that block's script tag has
    // been printed. Read it through a function rather than once at startup: the
    // call on ready below then sees it even when the ordering does not favour
    // us, and neither reader has to care which page it is on.
    //
    // Its fields can be empty strings - the API fills them in that way - so the
    // name is joined from what is actually there. Concatenating blindly gave
    // the agent a name that was a bare space.
    //
    // The object is expected on every page, so this does not guard against it
    // being absent so much as against it arriving in a shape nobody planned
    // for: a field that is null instead of a string, an id that is a numeric
    // zero, an object where text was expected. Whatever fails these checks is
    // simply not sent - an agent seeing nothing is better off than one seeing
    // "[object Object]" or "undefined undefined".
    const text = (value) => ('string' === typeof value || 'number' === typeof value)
        ? String(value).trim()
        : '';

    const playerFields = () => {
        if (typeof userobject === 'undefined' || !userobject || 'object' !== typeof userobject) {
            return null;
        }

        // An id of 0, '0' or '' is not a player. Only the string and number
        // cases are accepted, so an id that arrived as an object or an array
        // cannot pass by being truthy.
        const id = text(userobject.id);

        if ('' === id || '0' === id) {
            return null;
        }

        const name = [text(userobject.firstName), text(userobject.lastName)]
            .filter(Boolean)
            .join(' ');
        const email = text(userobject.email);

        return {
            name: name,

            // Not validation - just enough shape that what reaches the agent is
            // recognisably an address rather than whatever else ended up here.
            email: email.indexOf('@') > 0 ? email : ''
        };
    };

    if (config.identifyPlayer) {
        const player = playerFields();

        if (player) {
            window.__lc.visitor = { name: player.name, email: player.email };
        }
    }

    // A widget reporting visibility 'hidden' is not on the page at all - no
    // bubble, no button - so a greeting has nowhere to render and the visitor
    // sees nothing however well the campaign is configured. Nothing in this
    // theme puts it there: no hide or destroy call, no CSS hiding it, no such
    // flag in __lc. The vendor docs do not say what does, nor whether a greeting
    // can be shown while hidden; they say only that a maximize or minimize call
    // is how the state is left.
    //
    // 'minimize' and not 'maximize', deliberately: it restores the ordinary
    // bubble - the state a greeting appears over - without opening the window.
    // A window that opens afterwards is then the campaign's doing rather than
    // ours, which keeps the next diagnosis honest.
    // What state the widget is in. Tracked rather than asked for, because the
    // getter throws before the widget has loaded and openWidget() may well run
    // first.
    let visibility = null;
    let openedOnce = false;

    window.LiveChatWidget.on('visibility_changed', function (data) {
        visibility = data && data.visibility;
    });

    window.LiveChatWidget.on('ready', function (data) {
        const state = (data && data.state) || {};

        visibility = state.visibility || visibility;

        if (config.unhideOnReady && 'hidden' === state.visibility) {
            window.LiveChatWidget.call('minimize');
            visibility = 'minimized';
            say('was hidden -> minimize sent');
        }
    });

    // ---------------------------------------------------------------------
    // Opening the window on an arriving message.
    //
    // A greeting shows as a bubble and leaves the window closed, which is the
    // point of a greeting - and also why a player can miss it entirely. WHEN a
    // message appears is decided by the campaign configured in LiveChat;
    // nothing here inspects its rules, so changing a campaign needs no code
    // change. This only reacts to one arriving.
    // ---------------------------------------------------------------------
    const openWidget = (why) => {
        if (config.openOnce && openedOnce) {
            return;
        }

        // Already open: a second maximize would do nothing except make the next
        // person reading the diagnostics wonder what fired twice.
        if ('maximized' === visibility) {
            return;
        }

        openedOnce = true;
        window.LiveChatWidget.call('maximize');
        say('opened on ' + why);
    };

    if (config.openOnGreeting) {
        window.LiveChatWidget.on('greeting_displayed', function (greeting) {
            openWidget('greeting ' + (greeting && greeting.id));
        });
    }

    if (config.openOnAgentMessage) {
        window.LiveChatWidget.on('new_event', function (event) {
            // author.type separates an agent or bot from the player's own
            // messages; a system event carries no author at all.
            if (event && event.author && 'agent' === event.author.type) {
                openWidget('agent ' + event.type);
            }
        });
    }

    // ---------------------------------------------------------------------
    // Session variables have exactly one owner: collectSessionVariables().
    //
    // set_session_variables REPLACES the whole set - the vendor's words are
    // "creates new session variables, or replaces the ones set previously".
    // Anything sending a second set from somewhere else therefore wipes what
    // the first one wrote, and which survives depends on the order the ready
    // handlers happened to be registered in. That is not a bug you find by
    // reading either call; it is a bug you find when a report goes empty.
    //
    // So there is one set, sent once, from here. Anything that changes a
    // variable later - a campaign firing mid-session, in a later step of the
    // plan - uses update_session_variables, which modifies without replacing.
    //
    // Contributors add their keys inside this function. They must not call
    // LiveChat directly.
    // ---------------------------------------------------------------------

    // Chat session clock, reported so BA can split the chat stats by day and
    // hour. A zone is stated explicitly rather than taken from the visitor's
    // machine: the reporting has to line up with one timeline, whichever region
    // the visitor is in. An IANA name carries its own DST switch, so there is no
    // clock-change arithmetic to keep in sync here.
    //
    // It comes from the settings page, which is what its field promises: one
    // zone driving the Day and Hour variables AND the campaign reset boundaries
    // below, so a flag clears on the same day the report rolls over. It used to
    // be pinned to Europe/London here while the campaigns already followed the
    // setting - the same value as the default, so nothing looked wrong until
    // somebody changed it.
    //
    // Probed rather than trusted. PHP validates the zone against
    // timezone_identifiers_list() before it is stored, but an unusable one makes
    // Intl throw, and here that would happen inside the ready handler - no
    // reporting at all, and a console error on a device with no console to read
    // it. Probed once; the campaign formatter reuses the result.
    const timezone = (function () {
        const wanted = config.timezone || 'Europe/London';

        try {
            new Intl.DateTimeFormat('en-GB', { timeZone: wanted });

            return wanted;
        } catch (e) {
            return 'Europe/London';
        }
    }());

    const optionsDay = { weekday: 'long', timeZone: timezone };

    // hourCycle is stated rather than left to the locale. en-GB resolves to h23
    // on current ICU, which gives 00 through 23 - but older builds resolved the
    // same locale to h24, where midnight formats as "24". That is not a parse
    // failure anyone would notice: Number('24') is a perfectly good number that
    // no filter over 0-23 matches, so a rule for the small hours would silently
    // skip every session opened between 00:00 and 00:59 on those devices.
    const optionsHour = { hour: 'numeric', hourCycle: 'h23', timeZone: timezone };

    // ---------------------------------------------------------------------
    // Diagnostics. Off unless switched on in the settings, and even then only
    // for a user who can manage options - PHP gates it on the capability, not
    // just the setting, so a box left ticked cannot put this over a player's
    // page.
    //
    // It exists because a phone has no console to read, and the phone is the
    // device where this integration actually fails: the loading gates it used
    // to sit behind were invisible on a desktop.
    // ---------------------------------------------------------------------
    let badge = null;

    const say = (message) => {
        if (!config.debug) {
            return;
        }

        if (!badge) {
            badge = document.createElement('div');
            badge.setAttribute('style', [
                'position:fixed', 'top:0', 'left:0', 'z-index:2147483000',
                'max-width:70vw', 'padding:6px 8px',
                'font:12px/1.35 monospace', 'color:#fff', 'background:rgba(0,0,0,.82)',
                'white-space:pre-wrap', 'pointer-events:none'
            ].join(';'));
            (document.body || document.documentElement).appendChild(badge);
        }

        badge.textContent = message;
    };

    // ---------------------------------------------------------------------
    // Campaign flags.
    //
    // A campaign that shows its greeting sets a variable for that visitor; the
    // flag then expires on its own schedule. The cookie is the source of truth,
    // not the session variable: the vendor does not document whether session
    // variables survive a page load, so the flag is re-applied on every load
    // rather than assumed to still be there.
    //
    // Each flag stores the period it was set in. Periods are per campaign, so
    // one can expire weekly while its neighbour expires monthly, and adding a
    // row cannot disturb the others.
    // ---------------------------------------------------------------------
    const COOKIE = 'lc_campaign_flags';
    const COOKIE_DAYS = 60;
    const campaigns = Array.isArray(config.campaigns) ? config.campaigns : [];
    const expiredValue = typeof config.expiredValue === 'string' ? config.expiredValue : '0';

    // A value that is written as a number is sent as one. The theme already
    // does this for Hour, because a filter comparing numerically sorts '9'
    // above '14' when it is handed strings, and a rule written as
    // `win_campaign == 1` is the case that matters here.
    //
    // The round-trip is the guard: only a value whose numeric form is written
    // exactly the way it was typed converts. '1' becomes 1, but '007' stays
    // '007' and '1.50' stays '1.50' - silently turning those into 7 and 1.5
    // would change what the operator entered, and nothing downstream would show
    // that it had happened.
    const typed = (value) => {
        const asText = String(value);

        return '' !== asText && String(Number(asText)) === asText ? Number(asText) : asText;
    };

    // The calendar date in the reporting timezone. Everything below works on
    // that date rather than on wall-clock instants, which is what keeps the
    // March and October clock changes from needing a special case.
    // The zone was probed where it was resolved, so this cannot throw and needs
    // no fallback of its own - it used to carry one to UTC, which would have put
    // the reset boundaries in a different zone from the Day and Hour variables
    // in the one case it fired. Resolved once, not per call.
    const zoneFormat = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const zonedDate = (now) => {
        const parts = {};

        zoneFormat.formatToParts(now).forEach(function (part) {
            parts[part.type] = part.value;
        });

        return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
    };

    const asKey = (date) => date.getUTCFullYear()
        + '-' + ('0' + (date.getUTCMonth() + 1)).slice(-2)
        + '-' + ('0' + date.getUTCDate()).slice(-2);

    // Start of the period the given moment falls in. Two flags carrying
    // different keys for the same campaign means the period rolled over.
    const periodKey = (campaign, now) => {
        if (campaign.reset_type === 'never') {
            return 'never';
        }

        const today = zonedDate(now);

        if (campaign.reset_type === 'day') {
            return asKey(today);
        }

        if (campaign.reset_type === 'week') {
            // getUTCDay is 0 for Sunday; the setting is ISO, 1 for Monday.
            const iso = ((today.getUTCDay() + 6) % 7) + 1;
            today.setUTCDate(today.getUTCDate() - ((iso - Number(campaign.reset_day) + 7) % 7));

            return asKey(today);
        }

        // Monthly. A reset day of 29-31 does not exist in every month, and
        // skipping the reset would leave the flag stuck until a long month came
        // round, so it lands on the last day instead - against the real month,
        // which is why this is not clamped when the setting is saved.
        const lastDay = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
        const year = today.getUTCFullYear();
        const month = today.getUTCMonth() + 1;
        const wanted = Number(campaign.reset_day);
        const thisMonth = Math.min(wanted, lastDay(year, month));

        if (today.getUTCDate() >= thisMonth) {
            return asKey(new Date(Date.UTC(year, month - 1, thisMonth)));
        }

        const prevMonth = 1 === month ? 12 : month - 1;
        const prevYear = 1 === month ? year - 1 : year;

        return asKey(new Date(Date.UTC(prevYear, prevMonth - 1, Math.min(wanted, lastDay(prevYear, prevMonth)))));
    };

    const readFlags = () => {
        const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE + '=([^;]*)'));

        if (!match) {
            return {};
        }

        try {
            const parsed = JSON.parse(decodeURIComponent(match[1]));

            return parsed && 'object' === typeof parsed ? parsed : {};
        } catch (e) {
            // Malformed: treat as absent and let the next write replace it.
            return {};
        }
    };

    const writeFlags = (flags) => {
        const expires = new Date(Date.now() + COOKIE_DAYS * 86400000).toUTCString();

        document.cookie = COOKIE + '=' + encodeURIComponent(JSON.stringify(flags))
            + ';path=/;expires=' + expires + ';SameSite=Lax'
            + ('https:' === location.protocol ? ';Secure' : '');
    };

    // Every configured variable, with the expired value where the flag is not
    // set or its period has passed. Present either way on purpose: a rule
    // reading it gets a value rather than undefined.
    const campaignVariables = () => {
        const now = new Date();
        const stored = readFlags();
        const kept = {};
        const vars = {};

        campaigns.forEach(function (campaign) {
            const flag = stored[campaign.variable];
            const current = periodKey(campaign, now);

            if (flag && flag.p === current) {
                vars[campaign.variable] = typed(flag.v);
                kept[campaign.variable] = flag;
            } else {
                vars[campaign.variable] = typed(expiredValue);
            }
        });

        // Writing back drops expired flags and any left behind by a campaign
        // that has since been removed from the settings.
        writeFlags(kept);

        return vars;
    };

    const collectSessionVariables = () => {
        // Read the clock when the widget becomes ready, not when the page loads.
        // A visit can start hours before the chat does - reading it here keeps a
        // session opened at 00:30 from being filed under the previous day.
        const date = new Date();

        const vars = campaignVariables();

        vars.Day = date.toLocaleDateString('en-GB', optionsDay);

        // Hour goes over as a number, per the ChatBot time-filter guide: the
        // story filter compares it numerically, and a string would sort '9'
        // above '14'.
        vars.Hour = Number(date.toLocaleString('en-GB', optionsHour));

        return Object.assign(vars, supportVars);
    };

    window.LiveChatWidget.on('ready', function () {
        widgetReady = true;
        const vars = collectSessionVariables();

        window.LiveChatWidget.call('set_session_variables', vars);
        say('ready\n' + JSON.stringify(vars));
    });

    // Identify again once the widget is up. __lc.visitor above only lands if
    // the player's details were already on the page when this module ran;
    // these calls do not care about that ordering, and a widget that loaded
    // seconds later through the fallback timer gets the same treatment.
    // Without either, the agent sees an anonymous visitor and per-player
    // targeting has nothing to match on.
    if (config.identifyPlayer) {
        window.LiveChatWidget.on('ready', function () {
            const player = playerFields();

            if (!player) {
                return;
            }

            if (player.name) {
                window.LiveChatWidget.call('set_customer_name', player.name);
            }

            if (player.email) {
                window.LiveChatWidget.call('set_customer_email', player.email);
            }
        });
    }

    // The campaign decides when it fires; this only records that it did. The
    // greeting's id is the campaign id configured on the settings page.
    //
    // update, not set: the whole set was established on ready and a second set
    // would replace it, taking Day and Hour with it.
    window.LiveChatWidget.on('greeting_displayed', function (greeting) {
        const id = greeting && Number(greeting.id);

        if (!id) {
            return;
        }

        const matched = campaigns.filter(function (campaign) {
            return Number(campaign.campaign_id) === id;
        });

        if (!matched.length) {
            return;
        }

        const now = new Date();
        const flags = readFlags();

        matched.forEach(function (campaign) {
            flags[campaign.variable] = {
                v: campaign.value_on,
                p: periodKey(campaign, now)
            };
        });

        writeFlags(flags);

        const vars = campaignVariables();

        window.LiveChatWidget.call('update_session_variables', vars);
        say('campaign ' + id + '\n' + JSON.stringify(vars));
    });

    // Defer loading tracking.js until the user shows intent to interact.
    // Loads exactly once on the first qualifying event, then detaches listeners.
    const triggerEvents = ['mousemove', 'scroll', 'click', 'touchstart', 'keydown'];
    let liveChatLoaded = false;
    let fallbackTimer = null;

    // A page opened in a background tab should not pay for the widget until it
    // is actually looked at. In the ordinary foreground case this never fires
    // and the timer below is what does the work.
    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            loadLiveChat();
        }
    };

    const loadLiveChat = () => {
        if (liveChatLoaded) {
            return;
        }
        liveChatLoaded = true;
        triggerEvents.forEach(function (evt) {
            window.removeEventListener(evt, loadLiveChat);
        });
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (fallbackTimer !== null) {
            clearTimeout(fallbackTimer);
            fallbackTimer = null;
        }
        window.LiveChatWidget.init();
        say('loading tracking.js');
    };

    triggerEvents.forEach(function (evt) {
        window.addEventListener(evt, loadLiveChat, { passive: true });
    });

    document.addEventListener('visibilitychange', onVisibilityChange);

    // The backstop. Those five events all need a visitor who does something,
    // and a phone has no mousemove at all - land on a page, read it without
    // scrolling, and the widget never loaded. No session meant the visitor did
    // not exist for LiveChat, so no campaign could reach them and the agent
    // could not see them either. Everything above still wins the race in the
    // common case; this only covers the visitor who does nothing.
    //
    // Configured on the settings page. It cannot realistically be absent: the
    // integration already returned above if the config did not print, and the
    // option carries a default - so a missing value means something is wrong
    // and the old interaction-only behaviour is the safer thing to fall back to.
    const fallbackMs = Number(config.fallbackMs) || 0;
    if (fallbackMs > 0) {
        fallbackTimer = setTimeout(loadLiveChat, fallbackMs);
    }

    // A way for other bundles to open the chat for a player stuck on something only an agent
    // can fix (the profile's attempts used up: support-offer.js). Present only while the chat
    // is on - this module is not enqueued at all when it is switched off - so a caller that
    // finds no bridge shows no button. The reason and party id go to the agent as session
    // variables; the draft waits in the message box for the player to send.
    window.bbSupport = {
        open: function (reason, draft, partyId) {
            supportVars = { support_reason: String(reason || '') };
            if (partyId) {
                supportVars.party_id = String(partyId);
            }
            if (widgetReady) {
                window.LiveChatWidget.call('update_session_variables', supportVars);
            }
            // Not loaded yet: load now. The maximize waits in the stub's queue until it is.
            loadLiveChat();
            if (draft) {
                window.LiveChatWidget.call('maximize', { messageDraft: String(draft) });
            } else {
                window.LiveChatWidget.call('maximize');
            }
            say('support offer opened: ' + supportVars.support_reason);
            return true;
        }
    };
}

// The same readiness guard the theme already uses for initFlexScrollContainers().
// Module bundles are served with `defer` (class-theme.php:246), so in practice
// this runs before DOMContentLoaded and takes the listener branch.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLiveChat);
} else {
    initLiveChat();
}
